import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2 } from "lucide-react";
import { IMAGES } from "@/lib/data";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const MUST_HOLD = [
  "All participating institutions sign governance, medical-direction, data-use, and exercise agreements before training begins.",
  "The crosswalk conflicts with no controlling law, scope of practice, medical direction, or current agency protocol.",
  "Primary measures demonstrate acceptable inter-rater reliability and data completeness before any outcome comparison.",
  "At least one prespecified primary readiness endpoint improves measurably, with no deterioration in safety endpoints.",
];

const WHAT_NEXT = [
  "Retention testing determines whether gains persist; recertification intervals are set to measured decay, not assumed decay.",
  "Independent evaluation finds the package feasible and states the conditions required for replication.",
  "No expansion occurs solely because the pilot was completed. Expansion follows the evidence or does not happen.",
];

const AGENDA = [
  "Establish measure reliability",
  "Quantify skill retention",
  "Test whether joint exercises improve interagency performance",
  "Evaluate rural adaptations",
  "Assess implementation cost",
  "Investigate appropriately governed links between readiness and clinical outcomes",
];

export default function Research() {
  return (
    <main data-testid="research-page">
      <PageHead
        chapter="06"
        eyebrow="Research and Evaluation"
        titleLines={["An evaluation designed", "to be believed."]}
        lede="No single proponent can legitimately establish a clinical standard. The evaluation is independent by design, and it publishes null and negative findings alongside favorable ones."
        image={IMAGES.commandCenter}
      />

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Eyebrow>Evaluation design</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
            Prospective, prespecified, independently controlled.
          </h2>
          <p className="mt-6 text-sm text-slate-400 leading-relaxed">
            At minimum, the pilot uses a prospective pre/post design with repeated measures, blinded or independent scoring where feasible, and a prespecified analysis plan. If enough agencies participate, a phased or stepped-wedge rollout strengthens inference by allowing later-starting groups to serve as temporary comparators. The independent evaluator — not the program — controls the analysis and reports the limitations.
          </p>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed">
            The primary endpoint is an observable readiness measure directly affected by training, such as time to effective hemorrhage control in a standardized scenario, rather than mortality. Secondary endpoints address coordination, retention, data quality, and implementation feasibility. Patient outcomes may be explored only when legal authority, data quality, sample size, and ethics review support it.
          </p>
          <div className="mt-10 relative overflow-hidden border border-line">
            <img src={IMAGES.gearDetail} alt="Tactical medical kit detail" className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700" />
            <span className="absolute bottom-0 left-0 bg-ink/80 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 px-4 py-2">
              Primary endpoint · Hemorrhage-control interval
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <Eyebrow>Predefined success conditions</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
            Success is defined before data collection begins.
          </h2>
          <div className="mt-8 border border-line bg-surface p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-6">Conditions that must hold</p>
            <ul className="space-y-4">
              {MUST_HOLD.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                  <CheckCircle2 size={15} className="text-signal mt-0.5 shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 border border-line bg-surface p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-6">Conditions on what happens next</p>
            <ul className="space-y-4">
              {WHAT_NEXT.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                  <CheckCircle2 size={15} className="text-signal mt-0.5 shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="border-y border-line bg-surface py-24" data-testid="research-agenda-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Eyebrow>Limitations</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
              Stated directly, not implied away.
            </h2>
            <p className="mt-6 text-sm text-slate-400 leading-relaxed">
              T-EMRP has not demonstrated reliability across sites, persistence of training gains, cost-effectiveness, acceptability to U.S. agencies, or any effect on patient outcomes. A pilot sample may be too small for mortality analysis. Local law, scope of practice, labor rules, records law, and medical direction vary by jurisdiction. Any composite readiness index remains a hypothesis until construct validity, weighting, sensitivity, and fairness are demonstrated.
            </p>
            <div className="mt-8 border-l-2 border-signal bg-ink p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">Publication commitment</p>
              <p className="text-sm text-slate-300 leading-relaxed">
                The evaluation publishes positive, negative, and null findings. A partner institution should hold the program to this commitment.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Future research agenda</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
              Sequential by design.
            </h2>
            <div className="mt-8 border border-line divide-y divide-line bg-ink">
              {AGENDA.map((a, i) => (
                <div key={i} data-testid={`agenda-step-${i}`} className="flex items-center gap-5 p-5 hover:bg-surface transition-colors">
                  <span className="font-mono text-xs text-signal">0{i + 1}</span>
                  <span className="text-sm text-slate-300">{a}</span>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="research-cta-contact" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Discuss a Research Role <ArrowUpRight size={15} />
              </Link>
              <Link to="/governance" data-testid="research-cta-governance" className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Governance Model <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
